// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { CodeableConcept, Coding, Condition, Patient } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useMemo, useState } from 'react';
import { DiagnosisCodeInput } from '../../../components/encounter/DiagnosisCodeInput';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import { ICD10_VALUESET } from '../../../constants/medical-info';
import { useRole } from '../../../hooks/useRole';
import { buildIcd10Concept, ICD10_SYSTEM } from '../../../utils/icd10';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';

const PAGE_SIZE = 10;

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getConditionTitle(condition: Condition): string {
  return condition.code?.text ?? condition.code?.coding?.[0]?.display ?? condition.code?.coding?.[0]?.code ?? '—';
}

function getIcd10Code(condition: Condition): string {
  return (
    condition.code?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/icd-10-cm')?.code ??
    condition.code?.coding?.[0]?.code ??
    '—'
  );
}

function getRecorderName(condition: Condition): string {
  return condition.recorder?.display ?? '—';
}

export interface DiagnosesSectionProps {
  patient: Patient;
  diagnoses: Condition[];
  loading: boolean;
  onChanged: (diagnoses: Condition[]) => void;
  onRefresh: () => void;
}

export function DiagnosesSection({
  patient,
  diagnoses,
  loading,
  onChanged,
  onRefresh,
}: DiagnosesSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState<CodeableConcept | undefined>();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Condition | undefined>();
  const [deletingId, setDeletingId] = useState<string | undefined>();
  const [conceptInputVersion, setConceptInputVersion] = useState(0);

  const [noteView, setNoteView] = useState<Condition | undefined>();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return diagnoses;
    }
    return diagnoses.filter((d) =>
      `${getConditionTitle(d)} ${getIcd10Code(d)} ${getRecorderName(d)}`.toLowerCase().includes(q)
    );
  }, [diagnoses, query]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const resetAdd = (): void => {
    setCode(undefined);
    setNote('');
  };

  const handleCodeResolved = (coding: Coding): void => {
    setCode(buildIcd10Concept(coding));
    setConceptInputVersion((version) => version + 1);
  };

  const handleAdd = async (): Promise<void> => {
    if (!code) {
      showNotification({ color: 'red', message: 'Please select an ICD-10 condition' });
      return;
    }
    setSaving(true);
    try {
      const today = dayjs().format('YYYY-MM-DD');
      const condition: Condition = {
        resourceType: 'Condition',
        subject: createReference(patient),
        code,
        recordedDate: today,
        recorder: profile ? createReference(profile) : undefined,
        note: note.trim() ? [{ text: note.trim() }] : undefined,
      };
      const created = await medplum.createResource<Condition>(withTenantScope(condition));
      onChanged([created, ...diagnoses]);
      onRefresh();
      setAddOpen(false);
      resetAdd();
      showNotification({ color: 'green', message: 'Diagnosis added' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (condition: Condition): void => {
    setEditing(condition);
    setCode(condition.code);
    setNote(condition.note?.[0]?.text ?? '');
  };

  const handleEditClose = (): void => {
    setEditing(undefined);
    resetAdd();
  };

  const handleEditSave = async (): Promise<void> => {
    if (!editing?.id) {
      return;
    }
    if (!code) {
      showNotification({ color: 'red', message: 'Please select an ICD-10 condition' });
      return;
    }
    setSaving(true);
    try {
      const updated: Condition = {
        ...editing,
        code,
        note: note.trim() ? [{ text: note.trim() }] : undefined,
      };
      const saved = await medplum.updateResource<Condition>(updated);
      onChanged(diagnoses.map((diagnosis) => (diagnosis.id === saved.id ? saved : diagnosis)));
      onRefresh();
      handleEditClose();
      showNotification({ color: 'green', message: 'Diagnosis updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (condition: Condition): Promise<void> => {
    if (!condition.id) {
      return;
    }
    setDeletingId(condition.id);
    try {
      await medplum.deleteResource('Condition', condition.id);
      onChanged(diagnoses.filter((diagnosis) => diagnosis.id !== condition.id));
      onRefresh();
      showNotification({ color: 'green', message: 'Diagnosis deleted' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setDeletingId(undefined);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Diagnoses</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              setPage(1);
            }}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={240}
            aria-label="Search diagnoses"
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)} disabled={!canEdit}>
            Add Diagnosis
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Condition</Table.Th>
                <Table.Th>ICD-10</Table.Th>
                <Table.Th>Diagnosis Date</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginated.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" c="dimmed" py="md">
                      No diagnoses on file.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginated.map((condition) => {
                  return (
                    <Table.Tr key={condition.id}>
                      <Table.Td>{getConditionTitle(condition)}</Table.Td>
                      <Table.Td>{getIcd10Code(condition)}</Table.Td>
                      <Table.Td>{formatDate(condition.recordedDate)}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <TableAnchor
                            component="button"
                            type="button"
                            size="sm"
                            onClick={() => setNoteView(condition)}
                            disabled={!condition.note?.[0]?.text}
                          >
                            View Note
                          </TableAnchor>
                          <ActionIcon
                            aria-label={`Edit diagnosis ${getConditionTitle(condition)}`}
                            color="gray"
                            disabled={!canEdit}
                            variant="subtle"
                            onClick={() => handleEditOpen(condition)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                          <ActionIcon
                            aria-label={`Delete diagnosis ${getConditionTitle(condition)}`}
                            color="red"
                            disabled={!canEdit || deletingId === condition.id}
                            loading={deletingId === condition.id}
                            variant="subtle"
                            onClick={() => handleDelete(condition)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {filtered.length > PAGE_SIZE && (
        <Group justify="space-between" mt="sm">
          <Text size="sm" c="dimmed">{`${filtered.length} of ${diagnoses.length}`}</Text>
          <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}

      <Modal
        opened={addOpen}
        onClose={() => {
          setAddOpen(false);
          resetAdd();
        }}
        title="Add Diagnosis"
        centered
      >
        <Stack gap="md">
          <DiagnosisCodeInput
            label="ICD-10 Condition"
            value={code?.coding?.find((coding) => coding.system === ICD10_SYSTEM)?.code}
            onResolve={handleCodeResolved}
          />
          <CodeableConceptInput
            key={`add-diagnosis:${conceptInputVersion}`}
            binding={ICD10_VALUESET}
            defaultValue={code}
            label="Diagnosis name"
            name="diagnosis"
            path="Condition.code"
            placeholder="Search ICD-10 codes…"
            onChange={setCode}
          />
          <Textarea
            label="Note (optional)"
            placeholder="Clinical notes about this diagnosis…"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setAddOpen(false);
                resetAdd();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} loading={saving} disabled={!code}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={Boolean(editing)} onClose={handleEditClose} title="Edit Diagnosis" centered>
        <Stack gap="md">
          <DiagnosisCodeInput
            label="ICD-10 Condition"
            value={code?.coding?.find((coding) => coding.system === ICD10_SYSTEM)?.code}
            onResolve={handleCodeResolved}
          />
          <CodeableConceptInput
            key={`${editing?.id ?? 'edit-diagnosis'}:${conceptInputVersion}`}
            binding={ICD10_VALUESET}
            defaultValue={editing?.code}
            label="Diagnosis name"
            name="edit-diagnosis"
            path="Condition.code"
            placeholder="Search ICD-10 codes…"
            onChange={setCode}
          />
          <Textarea
            label="Note (optional)"
            placeholder="Clinical notes about this diagnosis…"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={handleEditClose}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} loading={saving} disabled={!code}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={Boolean(noteView)} onClose={() => setNoteView(undefined)} title="Diagnosis Note" centered>
        <Text>{noteView?.note?.[0]?.text ?? 'No note recorded.'}</Text>
      </Modal>
    </Paper>
  );
}
